import { AuthInfo } from "@modelcontextprotocol/sdk/server/auth/types.js";
import { MissingCredentialsError } from "./errors.js";
import { internalRequest } from "../api/api.js";
import { InvalidCredentialsError, NotFoundError } from "../api/errors.js";

export type ClientTokenCredentials = {
  type: "client_token";
  token: string;
  client: string;
  origin?: "local" | "remote";
};

export type JwtCredentials = {
  type: "jwt";
  jwt: string;
  styleguideId: number;
  styleguideName?: string;
  features: Record<string, boolean>;
  viewerId?: string;
  userId?: number;
  teamId?: number;
  mcpAuthId?: number;
  tokenSets?: Array<{ id: number; name: string }>;
  clientId?: undefined;
};

export type OauthJwtCredentials = {
  type: "oauth_jwt";
  jwt: string;
  clientId: string;
  features: Record<string, boolean>;
  userId?: number;
  teamId: number;
  viewerId?: string;
  accessRestrictedReason?: string;
  styleguideId?: undefined;
  mcpAuthId?: undefined;
};

export type Credentials =
  | ClientTokenCredentials
  | JwtCredentials
  | OauthJwtCredentials;

export function isLocalMCP(
  credentials: Credentials,
): credentials is Extract<Credentials, { type: "client_token" }> {
  return credentials.type === "client_token" && credentials.origin !== "remote";
}

export function getCredentialsFromEnvironment(): Credentials {
  const token = process.env["ZEROHEIGHT_ACCESS_TOKEN"];
  const client = process.env["ZEROHEIGHT_CLIENT_ID"];
  if (!token || !client) {
    throw new MissingCredentialsError();
  }

  return {
    type: "client_token",
    token,
    client,
    origin: "local",
  };
}

export const showLockedPagesForCredentialType = (credentials: Credentials) =>
  credentials.type === "oauth_jwt";

export const getShowLockedParam = (credentials: Credentials) =>
  showLockedPagesForCredentialType(credentials) ? "true" : "false";

interface JwtResponse {
  jwt: string;
  styleguide_id: number;
  styleguide_name?: string;
  features: Record<string, boolean>;
  user_id?: number;
  viewer_id?: string;
  team_id?: number;
  mcp_auth_id?: number;
  token_sets?: Array<{ id: number; name: string }>;
}

export async function getCredentials(
  authInfo?: AuthInfo,
): Promise<Credentials> {
  if (!authInfo) {
    return getCredentialsFromEnvironment();
  }
  if (authInfo.extra?.auth_type === "remote_api_key") {
    const token = authInfo.extra.access_token;
    const client = authInfo.extra.client_id;
    if (typeof token !== "string" || typeof client !== "string") {
      throw new InvalidCredentialsError();
    }

    return {
      type: "client_token",
      token,
      client,
      origin: "remote",
    };
  }
  if (authInfo.clientId === "http-client") {
    let res: JwtResponse;
    try {
      res = await internalRequest<JwtResponse>(
        "/mcp_url/exchange_token",
        "POST",
        {
          token: authInfo.token,
        },
      );
    } catch (e) {
      if (e instanceof NotFoundError) {
        throw new InvalidCredentialsError();
      }
      throw e;
    }

    return {
      type: "jwt",
      jwt: res.jwt,
      styleguideId: res.styleguide_id,
      styleguideName: res.styleguide_name,
      features: res.features ?? {},
      userId: res.user_id,
      viewerId: res.viewer_id,
      teamId: res.team_id,
      mcpAuthId: res.mcp_auth_id,
      tokenSets: res.token_sets,
    };
  }
  return {
    type: "oauth_jwt",
    jwt: authInfo.token,
    features: authInfo.extra!.features as Record<string, boolean>,
    userId: authInfo.extra!.user_id as number,
    teamId: authInfo.extra!.team_id as number,
    clientId: authInfo.clientId,
    viewerId: authInfo.extra!.viewer_id as string | undefined,
    accessRestrictedReason: authInfo.extra!.access_restricted_reason as
      | string
      | undefined,
  };
}
